import React, { useState, useEffect, useCallback } from 'react';
import {
  getBrandGuidesByProject,
  BrandGuide,
  setDefaultBrandGuide,
  activateBrandGuide,
  deleteBrandGuide
} from '../services/api';
import BrandGuidePreview from './BrandGuidePreview';
import { GlassCard } from './ui/GlassCard';
import { GlassButton } from './ui/GlassButton';
import { GlassBadge } from './ui/GlassBadge';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import './BrandGuidesList.css';

interface BrandGuidesListProps {
  projectId: string;
  onEdit?: (brandGuide: BrandGuide) => void;
  onCreate?: () => void;
}

const BrandGuidesList: React.FC<BrandGuidesListProps> = ({
  projectId,
  onEdit,
  onCreate
}) => {
  const [brandGuides, setBrandGuides] = useState<BrandGuide[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedGuide, setExpandedGuide] = useState<string | null>(null);
  const [previewGuide, setPreviewGuide] = useState<BrandGuide | null>(null);

  useEffect(() => {
    loadBrandGuides();
  }, [projectId]);

  const loadBrandGuides = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getBrandGuidesByProject(projectId);
      setBrandGuides(data);
    } catch (err) {
      console.error('Failed to load brand guides:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  const handleSetDefault = async (brandGuideId: string) => {
    try {
      await setDefaultBrandGuide(brandGuideId);
      loadBrandGuides();
    } catch (err) {
      alert('Failed to set default brand guide');
    }
  };

  const handleActivate = async (brandGuideId: string) => {
    try {
      await activateBrandGuide(brandGuideId);
      loadBrandGuides();
    } catch (err) {
      alert('Failed to activate brand guide');
    }
  };

  const handleDelete = async (brandGuideId: string) => {
    if (!window.confirm('Delete this brand guide? This action cannot be undone.')) return;

    try {
      await deleteBrandGuide(brandGuideId);
      loadBrandGuides();
    } catch (err) {
      alert('Failed to delete brand guide');
    }
  };

  const toggleExpand = (brandGuideId: string) => {
    setExpandedGuide(expandedGuide === brandGuideId ? null : brandGuideId);
  };

  if (isLoading) {
    return (
      <GlassCard variant="default" padding="lg" className="brand-guides-loading">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner" style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--stroke)',
            borderTop: '3px solid var(--primary-500)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: 'var(--text-secondary)' }}>Loading brand guides...</p>
        </div>
      </GlassCard>
    );
  }

  if (brandGuides.length === 0) {
    return (
      <GlassCard variant="default" padding="lg" className="brand-guides-empty">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: 500 }}>No Brand Guides Yet</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
            Create your first brand guide to maintain consistent branding across all landing pages and marketing materials.
          </p>
          {onCreate && (
            <GlassButton variant="primary" onClick={onCreate}>
              Create Brand Guide
            </GlassButton>
          )}
        </div>
      </GlassCard>
    );
  }

  return (
    <>
      <GlassCard variant="default" padding="md" className="brand-guides-list">
        <div className="brand-guides-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid var(--stroke)'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 500 }}>Brand Guides</h2>
          {onCreate && (
            <GlassButton variant="primary" size="sm" onClick={onCreate} icon={<Plus size={16} />}>
              New Brand Guide
            </GlassButton>
          )}
        </div>

        <div className="brand-guides-grid" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {brandGuides.map((guide) => (
            <GlassCard
              key={guide.brand_guide_id}
              variant={guide.is_default ? 'strong' : 'default'}
              padding="md"
              className={`brand-guide-card ${guide.is_default ? 'default' : ''}`}
            >
              <div
                className="brand-guide-header"
                onClick={() => toggleExpand(guide.brand_guide_id)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <div className="brand-guide-title" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 500 }}>{guide.brand_guide_name}</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {guide.is_default && <GlassBadge variant="success" size="sm">DEFAULT</GlassBadge>}
                    <GlassBadge variant={guide.status === 'active' ? 'success' : 'warning'} size="sm">
                      {guide.status}
                    </GlassBadge>
                  </div>
                </div>
                <button
                  className="expand-toggle"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {expandedGuide === guide.brand_guide_id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
              </div>

              {expandedGuide === guide.brand_guide_id && (
                <div className="brand-guide-details" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--stroke)' }}>
                  {/* Brand Identity */}
                  {guide.brand_name && (
                    <GlassCard variant="strong" padding="sm" style={{ marginBottom: '12px' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Brand Identity</h4>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Name:</strong> {guide.brand_name}</p>
                      {guide.tagline && <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Tagline:</strong> {guide.tagline}</p>}
                      {guide.brand_voice && <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Voice:</strong> {guide.brand_voice}</p>}
                    </GlassCard>
                  )}

                  {/* Colors */}
                  {(guide.primary_color || guide.secondary_color || guide.accent_color) && (
                    <GlassCard variant="strong" padding="sm" style={{ marginBottom: '12px' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Colors</h4>
                      <div className="color-swatches" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        {guide.primary_color && (
                          <div className="color-swatch" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="swatch" style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: 'var(--radius-md)',
                              backgroundColor: guide.primary_color,
                              border: '2px solid var(--stroke)'
                            }}></div>
                            <span style={{ fontSize: '13px', fontFamily: 'monospace' }}>{guide.primary_color}</span>
                          </div>
                        )}
                        {guide.secondary_color && (
                          <div className="color-swatch" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="swatch" style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: 'var(--radius-md)',
                              backgroundColor: guide.secondary_color,
                              border: '2px solid var(--stroke)'
                            }}></div>
                            <span style={{ fontSize: '13px', fontFamily: 'monospace' }}>{guide.secondary_color}</span>
                          </div>
                        )}
                        {guide.accent_color && (
                          <div className="color-swatch" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="swatch" style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: 'var(--radius-md)',
                              backgroundColor: guide.accent_color,
                              border: '2px solid var(--stroke)'
                            }}></div>
                            <span style={{ fontSize: '13px', fontFamily: 'monospace' }}>{guide.accent_color}</span>
                          </div>
                        )}
                      </div>
                    </GlassCard>
                  )}

                  {/* Typography */}
                  {(guide.primary_font || guide.secondary_font) && (
                    <GlassCard variant="strong" padding="sm" style={{ marginBottom: '12px' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Typography</h4>
                      {guide.primary_font && <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Primary Font:</strong> {guide.primary_font}</p>}
                      {guide.secondary_font && <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Secondary Font:</strong> {guide.secondary_font}</p>}
                    </GlassCard>
                  )}

                  {/* Source */}
                  <GlassCard variant="strong" padding="sm" style={{ marginBottom: '16px' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Source</h4>
                    <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Type:</strong> {guide.source_type || 'manual'}</p>
                    {guide.source_url && (
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        <strong>URL:</strong>{' '}
                        <a href={guide.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-500)' }}>
                          {guide.source_url}
                        </a>
                      </p>
                    )}
                    {guide.llm_model && <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Generated with:</strong> {guide.llm_model}</p>}
                  </GlassCard>

                  {/* Actions */}
                  <div className="brand-guide-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <GlassButton variant="primary" size="sm" onClick={() => setPreviewGuide(guide)}>
                      Preview
                    </GlassButton>
                    {onEdit && (
                      <GlassButton variant="secondary" size="sm" onClick={() => onEdit(guide)}>
                        Edit
                      </GlassButton>
                    )}
                    {!guide.is_default && (
                      <GlassButton variant="ghost" size="sm" onClick={() => handleSetDefault(guide.brand_guide_id)}>
                        Set as Default
                      </GlassButton>
                    )}
                    {guide.status !== 'active' && (
                      <GlassButton variant="ghost" size="sm" onClick={() => handleActivate(guide.brand_guide_id)}>
                        Activate
                      </GlassButton>
                    )}
                    <GlassButton variant="danger" size="sm" onClick={() => handleDelete(guide.brand_guide_id)}>
                      Delete
                    </GlassButton>
                  </div>
                </div>
              )}
            </GlassCard>
          ))}
        </div>
      </GlassCard>

      {/* Preview Modal */}
      {previewGuide && (
        <BrandGuidePreview
          brandGuide={previewGuide}
          onClose={() => {
            setPreviewGuide(null);
            loadBrandGuides();
          }}
        />
      )}
    </>
  );
};

export default BrandGuidesList;
